import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import DOMPurify from 'dompurify';
import {
  Clock,
  ExternalLink,
  Loader2,
  Share2,
  Volume2,
  Headphones,
  Headset,
  MoreHorizontal,
  VolumeX,
  Image as ImageIcon,
  Play,
  Pause,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { generateSpeechStream } from "@/services/textToSpeechService";
import { getRandomGradient } from "@/assets/gradients";
import gradient2 from "@/assets/gradient_2.png";
import gradient3 from "@/assets/gradient_3.png";
import gradient4 from "@/assets/gradient_4.png";
import gradient5 from "@/assets/gradient_5.png";
import gradient6 from "@/assets/gradient_6.png";
import Illustration_1 from "@/assets/Illustration_1.jpg";
import Illustration_2 from "@/assets/Illustration_2.jpg";
import Illustration_3 from "@/assets/Illustration_3.jpg";
import Illustration_4 from "@/assets/Illustration_4.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faListCheck as listCheckIcon,
  faPlay,
  faPause,
  faVolumeHigh,
  faVolumeXmark,
  faXmark,
  faBookmark as faBookmarkSolid,
  faRobot,
} from "@fortawesome/free-solid-svg-icons";
import { faBookmark } from "@fortawesome/free-regular-svg-icons";
import AiIcon from "/ai-svg-oreog.svg";
import {
  faThumbsUp as farThumbsUp,
  faThumbsDown as farThumbsDown,
} from "@fortawesome/free-regular-svg-icons";
import { useAudioStore } from "@/stores/audioStore";
import { LazyImage } from "@/components/LazyImage";
import { cn } from "@/lib/utils";
import { lockBodyScroll, unlockBodyScroll } from '@/utils/scrollLock';

// Audio state interface
interface AudioState {
  isPlaying: boolean;
  isLoading: boolean;
  audioElement: HTMLAudioElement | null;
  duration: number;
  currentTime: number;
  error: string | null;
  isExpanded: boolean;
  currentWordIndex: number;
  titleWords: string[];
  contentWords: string[];
}
interface Article {
  id?: string;
  title: string;
  description: string;
  url: string;
  imageUrl?: string;
  publishedAt: string;
  source: {
    name: string;
    domain: string;
  };
  country: string;
  language: string;
  aiSummary?: string;
}

interface ArticleCardProps {
  article: Article;
  onSummarize: (article: Article) => void;
  isGeneratingSummary?: boolean;
  isDarkMode?: boolean;
  prefetchUrls?: string[];
}

export const ArticleCard = ({
  article,
  onSummarize,
  isGeneratingSummary = false,
  isDarkMode = false,
  prefetchUrls,
}: ArticleCardProps) => {
  const navigate = useNavigate();
  const {
    currentlyPlayingArticleId,
    isAudioPlaying,
    setCurrentlyPlayingArticle,
    setIsAudioPlaying,
  } = useAudioStore();
  // Average words per second in speech
  const wordsPerSecond = 3;
  // Store the selected gradient in state to prevent it from changing on re-renders
  const [gradient] = useState(() => getRandomGradient(isDarkMode));
  const [audioState, setAudioState] = useState<AudioState>({
    isPlaying: false,
    isLoading: false,
    audioElement: null,
    duration: 0,
    currentTime: 0,
    error: null,
    isExpanded: false,
    currentWordIndex: 0,
    titleWords: [],
    contentWords: [],
  });
  const [isBookmarked, setIsBookmarked] = useState(() => {
    const bookmarkedArticles = JSON.parse(
      localStorage.getItem("bookmarkedArticles") || "{}"
    );
    return !!bookmarkedArticles[article.url];
  });

  const handleBookmark = useCallback(() => {
    const bookmarkedArticles = JSON.parse(
      localStorage.getItem("bookmarkedArticles") || "{}"
    );
    if (isBookmarked) {
      delete bookmarkedArticles[article.url];
    } else {
      bookmarkedArticles[article.url] = {
        ...article,
        bookmarkedAt: new Date().toISOString(),
      };
    }
    localStorage.setItem("bookmarkedArticles", JSON.stringify(bookmarkedArticles));

    // Animate the bookmark button with a smoother animation
    const button = document.getElementById(`bookmark-button-${article.url}`);
    if (button) {
      button.style.transform = "scale(0)";
      setTimeout(() => {
        setIsBookmarked(!isBookmarked);
        button.style.transform = "scale(1.2)";
        setTimeout(() => {
          button.style.transform = "scale(1)";
        }, 150);
      }, 150);
    } else {
      setIsBookmarked(!isBookmarked);
    }
  }, [article, isBookmarked]);
  const [imageError, setImageError] = useState(false);
  const [isMounted, setIsMounted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const isStandalone =
      typeof window !== "undefined" &&
      ((window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
        // @ts-ignore - iOS Safari standalone flag
        (typeof navigator !== "undefined" && (navigator as any).standalone));

    if (isFullscreen && !isStandalone) {
      lockBodyScroll();
      return () => {
        unlockBodyScroll();
      };
    }

    return () => { };
  }, [isFullscreen]);

  // Get random gradient for error fallback
  const [fallbackGradient] = useState(() => {
    const gradients = [gradient2, gradient3, gradient4, gradient5, gradient6, Illustration_1, Illustration_2, Illustration_3, Illustration_4,
    ];
    const randomIndex = Math.floor(Math.random() * gradients.length);
    return gradients[randomIndex];
  });

  useEffect(() => {
    // Set up audio element event listeners
    if (audioState.audioElement) {
      const element = audioState.audioElement;

      const handleTimeUpdate = () => {
        setAudioState((prev) => ({
          ...prev,
          currentTime: element.currentTime,
        }));
      };

      const handleDurationChange = () => {
        setAudioState((prev) => ({
          ...prev,
          duration: element.duration,
        }));
      };

      const handleEnded = () => {
        setAudioState((prev) => ({
          ...prev,
          isPlaying: false,
          currentTime: element.duration,
        }));
      };

      element.addEventListener("timeupdate", handleTimeUpdate);
      element.addEventListener("durationchange", handleDurationChange);
      element.addEventListener("ended", handleEnded);

      return () => {
        element.removeEventListener("timeupdate", handleTimeUpdate);
        element.removeEventListener("durationchange", handleDurationChange);
        element.removeEventListener("ended", handleEnded);
      };
    }
  }, [audioState.audioElement]);

  // Cleanup on unmount
  useEffect(() => {
    // Handle stopping audio when another card starts playing
    const handleStopAllAudio = () => {
      if (audioState.audioElement && audioState.isPlaying) {
        audioState.audioElement.pause();
        setAudioState((prev) => ({
          ...prev,
          isPlaying: false,
          isExpanded: false,
        }));
        setIsAudioPlaying(false);
        setCurrentlyPlayingArticle("");
      }
    };

    window.addEventListener("stopAllAudio", handleStopAllAudio);

    return () => {
      setIsMounted(false);
      // Cleanup audio when component unmounts
      if (audioState.audioElement) {
        audioState.audioElement.pause();
        audioState.audioElement.src = "";
        setIsAudioPlaying(false);
        setCurrentlyPlayingArticle("");
      }
      window.removeEventListener("stopAllAudio", handleStopAllAudio);
    };
  }, [
    audioState.audioElement,
    audioState.isPlaying,
    setIsAudioPlaying,
    setCurrentlyPlayingArticle,
  ]);

  const handleShare = async () => {
    const textToShare = article.aiSummary || article.description;
    const shareData = {
      title: article.title,
      text: textToShare,
      url: article.url,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback for browsers that don't support Web Share API
        await navigator.clipboard.writeText(
          `${shareData.title}\n\n${shareData.text}\n\n${shareData.url}`
        );
        toast.success("Link copied to clipboard");
      }
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        console.error("Error sharing:", err);
        toast.error("Failed to share. Please try again.");
      }
    }
  };

  const handleListen = useCallback(async () => {
    // If another audio is already playing or being generated, prevent new generation
    if (isAudioPlaying && !audioState.audioElement) {
      toast.error(
        "Please wait for the current audio to finish playing before generating a new one."
      );
      return;
    }

    // Global function to stop all other players
    const stopAllAudioElements = () => {
      document.querySelectorAll("audio").forEach((audio) => {
        if (audio !== audioState.audioElement) {
          audio.pause();
          audio.src = "";
        }
      });
      // Also dispatch an event to notify other cards
      window.dispatchEvent(new CustomEvent("stopAllAudio"));
    };

    // If we have an audioElement, check if it can be played
    if (audioState.audioElement) {
      if (audioState.isPlaying) {
        audioState.audioElement.pause();
        setAudioState((prev) => ({
          ...prev,
          isPlaying: false,
        }));
        setIsAudioPlaying(false);
      } else {
        stopAllAudioElements();
        try {
          // Check if we need to reset the audio source
          if (audioState.audioElement.ended) {
            audioState.audioElement.currentTime = 0;
          }
          await audioState.audioElement.play();
          setAudioState((prev) => ({
            ...prev,
            isPlaying: true,
            isExpanded: true,
          }));
          setIsAudioPlaying(true);
          setCurrentlyPlayingArticle(article.id || "");
        } catch (error) {
          console.error("Error playing audio:", error);
          // Only regenerate if there's a real error, not just ended state
          if (!audioState.audioElement.ended) {
            audioState.audioElement.src = "";
            setAudioState((prev) => ({
              ...prev,
              audioElement: null,
              isPlaying: false,
              error: null,
            }));
            setIsAudioPlaying(false);
            setCurrentlyPlayingArticle("");
          }
        }
      }
      return;
    }

    // If loading, do nothing
    if (audioState.isLoading) return;

    try {
      setAudioState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
        isExpanded: true,
      }));

      // Get the text and split into words - only use description/summary for highlighting
      const textContent = [article.aiSummary || article.description]
        .filter(Boolean)
        .join(". ");

      // Calculate estimated words per second based on average speech rate
      const wordsPerSecond = 3; // Adjust this based on actual speech rate

      let audioElement;
      try {
        audioElement = await generateSpeechStream(textContent, { provider: 'unrealspeech' });
      } catch (error) {
        console.error("Error generating speech:", error);
        setAudioState((prev) => ({
          ...prev,
          isLoading: false,
          error: "Failed to generate audio. Please try again.",
        }));
        toast.error("Failed to generate audio. Please try again.");
        return;
      }

      if (!isMounted) {
        audioElement.pause();
        audioElement.src = "";
        return;
      }

      audioElement.addEventListener("timeupdate", () => {
        setAudioState((prev) => ({
          ...prev,
          currentTime: audioElement.currentTime,
        }));
      });

      audioElement.addEventListener("loadedmetadata", () => {
        setAudioState((prev) => ({
          ...prev,
          duration: audioElement.duration,
        }));
      });

      audioElement.addEventListener("ended", () => {
        setAudioState((prev) => ({
          ...prev,
          isPlaying: false,
          currentTime: audioElement.duration,
        }));
      });

      await audioElement.play();

      // Split the text into words - separate title and content for proper highlighting
      const titleWords = article.title.split(/\s+/);
      const contentText = article.aiSummary || article.description;
      const contentWords = contentText.split(/\s+/);

      // For audio generation, combine title and content
      const textToSpeak = [article.title, contentText]
        .filter(Boolean)
        .join(". ");

      setAudioState({
        isPlaying: true,
        isLoading: false,
        audioElement,
        duration: audioElement.duration || 0,
        currentTime: 0,
        error: null,
        isExpanded: true,
        currentWordIndex: 0,
        titleWords,
        contentWords,
      });

      setIsAudioPlaying(true);
      setCurrentlyPlayingArticle(article.id || "");
      // Open global audio bar with this audio element
      try {
        window.dispatchEvent(
          new CustomEvent("openGlobalAudioBar", {
            detail: { audioElement, title: article.title },
          })
        );
      } catch { }
    } catch (error) {
      console.error("Error generating speech:", error);
      if (!isMounted) return;

      const errorMessage =
        error instanceof Error ? error.message : "Failed to generate audio";
      setAudioState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        isExpanded: false,
      }));
      setIsAudioPlaying(false);
      setCurrentlyPlayingArticle("");
      toast.error(`Audio generation failed: ${errorMessage}`);
    }
  }, [article, audioState.isLoading, isMounted]);

  const handleImageError = useCallback(() => {
    console.warn("Failed to load image, using fallback:", article.imageUrl);
    setImageError(true);
  }, [article.imageUrl]);

  const handleAudioEnd = useCallback(() => {
    if (audioState.audioElement) {
      setAudioState((prev) => ({
        ...prev,
        isPlaying: false,
        currentTime: prev.duration,
        currentWordIndex: prev.titleWords.length + prev.contentWords.length - 1,
      }));
      setIsAudioPlaying(false);
      setCurrentlyPlayingArticle("");
    }
  }, [audioState.audioElement, setIsAudioPlaying, setCurrentlyPlayingArticle]);

  const handleAudioPlayPause = useCallback(
    (shouldPlay: boolean) => {
      if (audioState.audioElement) {
        if (shouldPlay) {
          audioState.audioElement.play().catch(console.error);
        } else {
          audioState.audioElement.pause();
        }
        setAudioState((prev) => ({
          ...prev,
          isPlaying: shouldPlay,
          isExpanded: true,
        }));
        // Ensure global bar is visible for this audio element
        try {
          window.dispatchEvent(
            new CustomEvent("openGlobalAudioBar", {
              detail: { audioElement: audioState.audioElement, title: article.title },
            })
          );
        } catch { }
      }
    },
    [audioState.audioElement]
  );

  const handleTimeUpdate = useCallback((currentTime: number) => {
    setAudioState((prev) => ({
      ...prev,
      currentTime,
    }));
  }, []);

  // Handle seeking in the audio player
  const handleSeek = useCallback((time: number) => {
    const audioElement = document.querySelector("audio");
    if (audioElement) {
      (audioElement as HTMLAudioElement).currentTime = time;
      setAudioState((prev) => ({
        ...prev,
        currentTime: time,
      }));
    }
  }, []);

  const toggleAudioPlayer = useCallback(() => {
    setAudioState((prev) => ({
      ...prev,
      isExpanded: !prev.isExpanded,
      isPlaying: false, // Pause when minimizing
    }));
    if (audioState.audioElement) {
      audioState.audioElement.pause();
    }
  }, [audioState.audioElement]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "1 day ago";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Get the background style
  const backgroundStyle = {
    backgroundImage:
      article.imageUrl && !imageError
        ? `url('${article.imageUrl}')`
        : `url('${fallbackGradient}')`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };

  // Format time in seconds to MM:SS
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const iconButtonClass = cn(
    "relative p-2 h-9 w-9 rounded-xl transition-colors [&:hover_.tooltip]:opacity-100",
    isDarkMode
      ? "text-gray-200 hover:bg-white/10"
      : "text-slate-600 hover:bg-slate-200/70"
  );

  return (
    <Card
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-3xl border transition-all duration-300",
        isDarkMode
          ? "bg-[#202020] border-white/5 hover:border-primary/60 hover:shadow-[0_26px_52px_-24px_rgba(8,8,12,0.9)]"
          : "bg-white/95 border-slate-200/80 shadow-[0_18px_54px_-28px_rgba(15,23,42,0.25)] hover:border-primary/40 hover:shadow-[0_28px_62px_-30px_rgba(59,130,246,0.35)] backdrop-blur",
        audioState.isPlaying && "border-primary/70 shadow-primary/30"
      )}
    >
      <CardContent className="p-0 flex flex-col">
        <div
          className={cn(
            "relative rounded-3xl rounded-b-none p-3 transition-colors"
          )}
        >
          <div
            className="w-full h-full transition-transform duration-300 rounded-2xl"
            style={backgroundStyle}
          >
            <div
              className={cn(
                "relative w-full aspect-[16/9] rounded-2xl overflow-hidden select-none bg-gradient-to-br",
                isDarkMode
                  ? "from-slate-800/70 via-slate-900/80 to-slate-900/60"
                  : "from-slate-200/70 via-slate-300/60 to-slate-200/50"
              )}
            >
              <LazyImage
                draggable="false"
                src={
                  article.imageUrl && !imageError
                    ? article.imageUrl
                    : fallbackGradient
                }
                alt={article.title}
                className={cn(
                  "w-full h-full object-cover transition-all duration-300",
                  isFullscreen ? "opacity-0" : "cursor-zoom-in",
                  (!article.imageUrl || imageError) &&
                  (isDarkMode
                    ? "bg-gradient-to-br from-slate-950/70 via-slate-900/70 to-slate-950/70"
                    : "bg-gradient-to-br from-slate-900/60 via-slate-800/50 to-slate-900/40")
                )}
                onError={(e) => {
                  setImageError(true);
                  (e.currentTarget as HTMLImageElement).src = fallbackGradient;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (article.imageUrl && !imageError) {
                    setIsFullscreen(true);
                  }
                }}
                prefetchUrls={prefetchUrls}
                rootMargin="400px"
              />
            </div>

            {/* Fullscreen Image Modal */}
            {typeof document !== "undefined" &&
              isFullscreen &&
              createPortal(
                <div
                  className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center transition-opacity duration-200 opacity-100"
                  onClick={() => setIsFullscreen(false)}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "absolute top-4 right-4 h-10 w-10 rounded-full text-white transition-all duration-300",
                      isDarkMode ? "bg-black/40 " : "bg-black"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsFullscreen(false);
                    }}
                  >
                    <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                  </Button>
                  <img
                    draggable="false"
                    src={article.imageUrl}
                    className={cn(
                      "max-h-[90vh] max-w-[90vw] object-contain transition-all duration-300 scale-100 opacity-100",
                      isDarkMode ? "" : ""
                    )}
                    alt={article.title}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>,
                document.body
              )}
          </div>
          <div className="absolute bottom-4 left-4 overflow-hidden">
            <Badge
              variant="secondary"
              className={cn(
                "border-0 bg-transparent px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-white backdrop-blur",
                isDarkMode ? "hover:bg-white/15" : "hover:bg-black/30"
              )}
            >
              {article.source?.domain?.replace(
                /\.(com|io|news|net|org|co|in|ai|dev|ie|uk|us|ca|tech|edu|gov|tv)$/i,
                ""
              ) || article.source?.name || "Unknown"}
            </Badge>
          </div>
        </div>

        <div
          className={cn(
            "p-2.5 xs:p-4 bg-transparent"
          )}
        >
          <div className="flex flex-col items-start gap-3 mb-2">
            <div className="flex w-full items-center gap-2 text-xs uppercase tracking-wide">
              <Clock className="h-3.5 w-3.5 text-primary/80" />
              <span className="pr-2 py-1 rounded-full bg-transparent text-primary/90 font-medium">
                {formatDate(article.publishedAt)}
              </span>
              <Badge
                variant="outline"
                className="ml-auto text-[10px] font-semibold uppercase select-none tracking-[0.18em] text-primary/90 bg-primary/5"
              >
                {article.country?.toUpperCase() || "N/A"}
              </Badge>
            </div>

            <h3
              className={cn(
                "font-semibold text-[1.22rem] leading-tight line-clamp-3 transition-colors",
                isDarkMode ? "text-slate-100" : "text-slate-900",
                "group-hover:text-primary"
              )}
            >
              {article.title}
            </h3>
          </div>

          <div
            className={cn(
              "mb-4 overflow-hidden rounded-2xl border-0 bg-transparent px-0"
            )}
          >
            {isGeneratingSummary ? (
              <div className="flex justify-start py-2 px-4">
                <div
                  className={cn(
                    "px-4 py-2 rounded-3xl border backdrop-blur-sm transition-colors",
                    isDarkMode
                      ? "bg-white/5 border-primary/40 text-white"
                      : "bg-white border-slate-200 text-slate-700 shadow-sm"
                  )}
                >
                  <div className="flex items-center space-x-2">
                    <svg
                      className={cn(
                        "h-6 w-6 animate-spin",
                        isDarkMode ? "text-white" : "text-slate-600"
                      )}
                      viewBox="0 0 256 256"
                      stroke="currentColor"
                    >
                      <line x1="128" y1="32" x2="128" y2="64" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                      <line x1="195.9" y1="60.1" x2="173.3" y2="82.7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                      <line x1="224" y1="128" x2="192" y2="128" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                      <line x1="195.9" y1="195.9" x2="173.3" y2="173.3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                      <line x1="128" y1="224" x2="128" y2="192" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                      <line x1="60.1" y1="195.9" x2="82.7" y2="173.3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                      <line x1="32" y1="128" x2="64" y2="128" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                      <line x1="60.1" y1="60.1" x2="82.7" y2="82.7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                    </svg>
                    <span
                      className={cn(
                        "text-sm font-medium",
                        isDarkMode ? "text-white" : "text-slate-700"
                      )}
                    >
                      Summarizing...
                    </span>
                  </div>
                </div>
              </div>

            ) : article.aiSummary ? (
              <div
                className={cn(
                  "rounded-2xl py-2 px-2 bg-transparent border-0"
                )}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-2 w-2 bg-primary rounded-full animate-pulse"></div>
                  <p className="text-primary text-sm font-semibold">
                    AI Summary
                  </p>
                </div>
                <div
                  className={`text-foreground leading-relaxed prose prose-sm max-w-none [&>h4]:text-sm [&>h4]:font-semibold [&>h4]:text-primary [&>h4]:mb-2 [&>h4]:mt-3 [&>h4:first-child]:mt-0 [&>ul]:mb-3 [&>li]:text-sm [&>li]:mb-1 [&>li]:leading-relaxed [&>blockquote]:border-l-0 [&>blockquote]:border-accent [&>blockquote]:pl-3 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>blockquote]:bg-accent/5 [&>blockquote]:py-2 [&>blockquote]:my-2 [&>p]:text-sm [&>p]:leading-relaxed [&>p]:mb-2`}
                >
                  <div
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.aiSummary) }}
                  />
                </div>
              </div>
            ) : (
              <div className="h-[max-content] px-1 py-3">
                <p
                  className={cn(
                    "text-[16px]",
                    isDarkMode ? "text-slate-200" : "text-slate-700"
                  )}
                >
                  {article.description}
                </p>
              </div>
            )}
          </div>

          <div className="h-px w-full rounded-full bg-gradient-to-r from-transparent via-primary/35 to-transparent mb-3" />

          <div className="flex items-center mt-0 justify-center gap-0">
            <div className="flex-1">
              {!article.aiSummary && !isGeneratingSummary && (
                <div className="relative inline-flex items-center justify-center">
                  <Button
                    // variant={isDarkMode ? "secondary" : "outline"}
                    size="sm"
                    disabled
                    className={`peer flex items-center gap-2 transition-smooth rounded-2xl px-4 ${isDarkMode
                      ? "bg-white/10 text-gray-400 border border-white/10 cursor-not-allowed"
                      : "bg-white text-gray-400 border border-gray-200 cursor-not-allowed"
                      }`}
                  >
                    <FontAwesomeIcon
                      icon={listCheckIcon}
                      style={{ color: isDarkMode ? "#9ca3af" : "#9ca3af" }}
                      className="w-4 h-4"
                    />
                    Summarize
                  </Button>
                  <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 peer-hover:opacity-100 transition-opacity duration-200">
                    You don't have access
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center ml-1">
              {article.aiSummary && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={iconButtonClass}
                  onClick={() => {
                    // Store summary in localStorage for the chat page to pick up
                    localStorage.setItem("summaryToSend", JSON.stringify({
                      summary: article.aiSummary,
                      title: article.title,
                      source: article.source?.name || "Unknown Source"
                    }));
                    // Navigate to chat page without reload
                    navigate("/");
                  }}
                >
                  <img
                    src={AiIcon}
                    alt="AI"
                    className="w-4 h-4 text-primary"
                  />
                  {/* Tooltip */}
                  <div className="tooltip absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 transition-opacity duration-200 pointer-events-none z-10">
                    Ask AI
                  </div>
                </Button>
              )}
              <Button
                id={`bookmark-button-${article.url}`}
                variant="ghost"
                size="sm"
                className={cn(
                  iconButtonClass,
                  "transition-transform duration-200",
                  isBookmarked && "text-primary"
                )}
                onClick={handleBookmark}
              >
                <FontAwesomeIcon
                  icon={isBookmarked ? faBookmarkSolid : faBookmark}
                  className="w-4 h-4"
                />
                {/* Tooltip */}
                <div className="tooltip absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 transition-opacity duration-200 pointer-events-none z-10">
                  {isBookmarked ? "Remove Bookmark" : "Bookmark"}
                </div>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={iconButtonClass}
                onClick={handleShare}
              >
                <Share2 className="w-4 h-4" />
                {/* Tooltip */}
                <div className="tooltip absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 transition-opacity duration-200 pointer-events-none z-10">
                  Share
                </div>
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className={cn(iconButtonClass, audioState.isPlaying && "text-primary")}
                onClick={handleListen}
                disabled={audioState.isLoading}
              >
                {audioState.isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : audioState.isPlaying ? (
                  <Headset className="w-4 h-4" />
                ) : (
                  <Headphones className="w-4 h-4" />
                )}
                {/* Tooltip */}
                <div className="tooltip absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 transition-opacity duration-200 pointer-events-none z-10">
                  {audioState.isLoading ? "Generating..." : audioState.isPlaying ? "Pause" : "Listen"}
                </div>
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className={`relative p-2 h-8 w-8 ${isDarkMode ? "hover:bg-[#3a3a3a]" : "hover:bg-accent/50"
                  } [&:hover_.tooltip]:opacity-100`}
                onClick={() => window.open(article.url, "_blank")}
              >
                <ExternalLink className="w-4 h-4" />
                {/* Tooltip */}
                <div className="tooltip absolute left-1/2 -translate-x-2 bottom-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 transition-opacity duration-200 pointer-events-none z-10">
                  Open Article
                </div>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>

      {/* Audio player UI removed - handled by GlobalAudioBar */}
    </Card>
  );
};
